import React from 'react';

import { CssBaseline, ThemeProvider } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';

import '@total-typescript/ts-reset';

import type { IPublicClientApplication } from '@azure/msal-browser';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';
import { registerSW } from 'virtual:pwa-register';

import { AuthRecoveryNotice } from './auth/AuthRecoveryNotice';
import { isAuthInteractionPending, observeMsalInteractions, reportAuthFailure } from './configs/auth';
import { queryClient } from './configs/queryClient';
import { theme } from './configs/theme';
import { createMsalInstance } from './msal/bootstrapMsal';
import { setMsalInstance } from './msal/msalInstance';
import { observeClientIdChanges, prepareAuthUrl } from './msal/urlState';
import { initializeApplicationInsights, trackException } from './observability/applicationInsights';

registerSW();

const CHUNK_RELOAD_KEY = 'vite-preload-reload';
function reloadOnceForChunkError() {
  if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) return;

  sessionStorage.setItem(CHUNK_RELOAD_KEY, 'true');
  window.location.reload();
}

window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  reloadOnceForChunkError();
});

window.addEventListener('unhandledrejection', (event) => {
  const reason: unknown = event.reason;

  const message = reason instanceof Error ? reason.message : typeof reason === 'string' ? reason : '';

  if (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Importing a module script failed')
  ) {
    event.preventDefault();
    reloadOnceForChunkError();
  }
});

let initializedMsalInstance: IPublicClientApplication | null = null;
const reconcileClientIdChanges = observeClientIdChanges(
  () => initializedMsalInstance !== null && !initializedMsalInstance.getActiveAccount(),
  () => initializedMsalInstance !== null && !isAuthInteractionPending()
);
const clientId = prepareAuthUrl();
initializeApplicationInsights();

const root = createRoot(document.getElementById('root') as HTMLElement);

void createMsalInstance(clientId)
  .then(async (msalInstance) => {
    initializedMsalInstance = msalInstance;
    observeMsalInteractions(msalInstance);
    if (reconcileClientIdChanges()) {
      return;
    }
    setMsalInstance(msalInstance);

    const { App } = await import('./App');
    if (reconcileClientIdChanges()) {
      return;
    }

    root.render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <ErrorBoundary
            fallback={<h1>Something went wrong</h1>}
            onError={(error, info) => trackException(error, { componentStack: info.componentStack ?? '' })}
          >
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <App pca={msalInstance} />
            </ThemeProvider>
          </ErrorBoundary>
        </QueryClientProvider>
      </React.StrictMode>
    );
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  })
  .catch((error: unknown) => {
    const authError = reportAuthFailure(error, 'bootstrap');
    root.render(
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthRecoveryNotice error={authError} onRetry={() => window.location.reload()} />
      </ThemeProvider>
    );
  });
